<template>
    <div>
        <div class="action-header">
            <div class="search-container">
                <div class="search-input">
                    <v-text-field
                        v-model="search"
                        prepend-inner-icon="search"
                        :placeholder="this.$vuetify.lang.t('$vuetify.userManagement.searchByUserName')"
                        solo
                        height="40px"
                        @keyup.enter="getUsers()">
                    </v-text-field>
                </div>

                <div class="ml-3">
                    <v-btn
                        @click="searchUser()"
                        height="40px"
                        color="primary"
                        outlined>
                        {{ $vuetify.lang.t('$vuetify.userManagement.search') }}
                    </v-btn>
                </div>
            </div>
            <div>
                <v-btn
                    v-if="permissionCreate"
                    height="40px"
                    @click="createNewMember()"
                    color="primary"> + {{ $vuetify.lang.t('$vuetify.userManagement.createNewMember') }} </v-btn>
            </div>
        </div>
        <v-card class="content-container">
            <v-data-table
                :footer-props="{
                    'items-per-page-options': [10, 20, 30, 50, 100]
                }"
                @page-count="pageCount = $event"
                :page.sync="page"
                :headers="headers"
                :options.sync="options"
                :server-items-length="totalUser"
                :items="users"
                :loading="loading">
                <template v-slot:[`item.action`]="{ item }">
                    <div class="action">
                        <v-icon
                            v-if="canEditUser(item)"
                            class="mr-2"
                            @click="openEditUserModal(item)">
                            edit
                        </v-icon>
                        <v-icon
                            v-if="canEditUser(item)"
                            class="mr-2"
                            @click="openChangePasswordModal(item)">
                            vpn_key
                        </v-icon>
                        <v-icon
                            v-if="canDeleteUser(item)"
                            class="mr-2"
                            @click="openDeleteUserModal(item)">
                            delete
                        </v-icon>
                    </div>
                </template>
            </v-data-table>
        </v-card>
        <div class="pt-2 paginator-box">
            <div>
                <v-pagination v-model="page" :length="pageCount"></v-pagination>
            </div>
        </div>

        <changePasswordModal
            ref="changePasswordModal"
            :toUpdateUserId="toUpdateUserId"/>

        <user-form-modal
            ref="userFormModal"
            :mode="mode"
            :toUpdateUserId="toUpdateUserId"
            @modal-closed="modalIsClosed()"
            @success-updated="getUsers()"
            @success-created="getUsers()"/>

        <delete-modal
            ref="modalDelete"
            @success-deleted="deleteSuccess()"
            :deleteForm="deleteForm"/>
    </div>
</template>

<script>

import UserFormModal from '@/components/modal/UserFormModal'
import ChangePasswordModal from '@/components/modal/ChangePasswordModal'
import permissionConstant from '@/constants/permissions.js'
import DeleteModal from '@/components/modal/DeleteModal'

export default {
    name: 'UserList',

    components: {
        ChangePasswordModal,
        UserFormModal,
        DeleteModal
    },

    data () {
        return {
            search: '',
            deleteForm: {
                header: this.$vuetify.lang.t('$vuetify.userManagement.deleteUser'),
                content: '',
                path: ''
            },
            pageCount: 1,
            page: 1,
            options: {},
            users: [],
            totalUser: 0,
            loading: false,
            branch: null,
            interval: null,
            permissionCreate: true,
            permissionUpdate: true,
            permissionDelete: true,
            toUpdateUserId: null,
            mode: ''
        }
    },

    computed: {
        headers () {
            return [
                { text: this.$vuetify.lang.t('$vuetify.userManagement.userName'), value: 'user__username', width: '20%' },
                { text: this.$vuetify.lang.t('$vuetify.userManagement.branch'), value: 'branch', width: '20%' },
                { text: this.$vuetify.lang.t('$vuetify.userManagement.role'), value: 'user__roles__name', width: '15%' },
                { text: this.$vuetify.lang.t('$vuetify.userManagement.joinedAt'), value: 'created_at', width: '15%' },
                { text: this.$vuetify.lang.t('$vuetify.userManagement.contactNo'), value: 'phone_number', width: '15%' },
                { text: '', value: 'action', width: '15%', sortable: false }
            ]
        }
    },

    methods: {
        async searchUser () {
            this.options.page = 1
            await this.getUsers()
        },

        async getUsers () {
            this.loading = true
            const { sortBy, sortDesc, page, itemsPerPage } = this.options

            let url = `users?page=${page}&itemsPerPage=${itemsPerPage}`

            if (sortBy[0]) {
                url += `&sortBy=${sortBy[0]}`
            }

            if (sortDesc[0]) {
                url += `&sortDesc=${sortDesc[0]}`
            }

            if (this.search) {
                url += `&search=${this.search}`
            }

            const res = await this.axios.get(url)
            if (res && res.data && res.data.status === 'ok') {
                this.users = res.data.data
                this.totalUser = res.data.total
                this.loading = false
            }
        },

        checkPermission () {
            this.permissionCreate = this.$util.checkPermission(permissionConstant.userManagement.admin.create) || this.$util.checkPermission(permissionConstant.userManagement.hr.create)
        },

        canEditUser (user) {
            if (user.user__roles__name === permissionConstant.role.superadmin) {
                return false
            } else if (user.user__roles__name === permissionConstant.role.admin && this.$util.checkPermission(permissionConstant.userManagement.admin.update)) {
                return true
            } else if (user.user__roles__name === permissionConstant.role.hr && this.$util.checkPermission(permissionConstant.userManagement.hr.update)) {
                return true
            }
            return false
        },

        canDeleteUser (user) {
            if (user.user__roles__name === permissionConstant.role.superadmin) {
                return false
            } else if (user.user__roles__name === permissionConstant.role.admin && this.$util.checkPermission(permissionConstant.userManagement.admin.delete)) {
                return true
            } else if (user.user__roles__name === permissionConstant.role.hr && this.$util.checkPermission(permissionConstant.userManagement.hr.delete)) {
                return true
            }
            return false
        },

        createNewMember () {
            this.mode = 'create'
            this.$refs.userFormModal.openModal()
        },

        openEditUserModal (user) {
            this.mode = 'update'
            this.toUpdateUserId = user.id
            this.$refs.userFormModal.openModal()
        },

        openChangePasswordModal (user) {
            this.toUpdateUserId = user.id
            this.$refs.changePasswordModal.openModal()
        },

        modalIsClosed () {
            this.mode = ''
            this.toUpdateUserId = null
        },

        openDeleteUserModal (user) {
            this.deleteForm.content = `${this.$vuetify.lang.t('$vuetify.userManagement.wantToDelete')} "${user.user__username}"? `
            this.deleteForm.path = `user/delete/${user.id}`
            this.$refs.modalDelete.dialog = true
        },

        async deleteSuccess () {
            await this.getUsers()
            this.$notify({
                title: this.$vuetify.lang.t('$vuetify.userManagement.congratulation'),
                text: this.$vuetify.lang.t('$vuetify.userManagement.userIsDeleted'),
                type: 'success'
            })
        }
    },

    mounted () {
        this.checkPermission()
    },

    watch: {
        options: {
            async handler () {
                await this.getUsers()
            },
            deep: true
        }
    }
}
</script>

<style lang="scss" scoped>
::v-deep .v-input__control > .v-input__slot {
    min-height: auto !important;
    display: flex !important;
    align-items: center !important;
    box-shadow: none !important;
    border: 1px solid #a8a8a8 !important;
}
</style>
